import React, { memo } from 'react';
import type { SearchableInputProps } from '../types';

/**
 * SearchableInput component for creating searchable dropdown inputs
 * 
 * This component combines an input field with dropdown functionality,
 * allowing users to either directly input values or search through
 * options in a dropdown. It manages two states: the actual value
 * and the search term when the dropdown is open.
 * 
 * @example
 * <SearchableInput
 *   inputRef={ref}
 *   value={selectedValue}
 *   searchTerm={searchTerm}
 *   isOpen={isDropdownOpen}
 *   placeholder="Search..."
 *   onChange={handleValueChange}
 *   onSearchChange={handleSearch}
 *   onFocus={handleFocus}
 *   onBlur={handleBlur}
 *   dropdownContent={<DropdownList items={filteredItems} />}
 * />
 */
export const SearchableInput = memo<SearchableInputProps>(({
  inputRef,
  value,
  searchTerm,
  isOpen,
  placeholder,
  onChange,
  onSearchChange,
  onFocus,
  onBlur,
  dropdownContent
}) => (
  <div className="relative w-full">
    <input
      ref={inputRef}
      type="text"
      value={isOpen ? searchTerm : value}
      onChange={(e) => {
        const newValue = e.target.value;
        if (!isOpen) {
          onChange(newValue);
        }
        onSearchChange(newValue);
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={placeholder}
      className="block w-full h-10 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-base text-gray-800 dark:text-gray-200 shadow-sm focus:outline-none focus:border-blue-500 focus:ring focus:ring-blue-500"
    />
    {dropdownContent}
  </div>
));

// Set display name for debugging purposes
SearchableInput.displayName = 'SearchableInput';